import { computed, nextTick, ref, watch } from 'vue'

export const COLOR_PREFERENCE_KEY = 'nr-color-preference'
export const LEGACY_COLOR_MODE_KEY = 'nr-color-mode'

const preference = ref('system')
const mode = ref('dark')

let initialized = false
let mediaQuery = null
let themeChangeHandler = null

function systemMode() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function storedPreference() {
  try {
    const stored = localStorage.getItem(COLOR_PREFERENCE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored

    // The old switch only recorded light mode; every other value meant "follow system".
    if (localStorage.getItem(LEGACY_COLOR_MODE_KEY) === 'light') return 'light'
  } catch {
    // Storage can be unavailable in private/restricted browser contexts.
  }
  return 'system'
}

function resolvedMode(value) {
  return value === 'light' || value === 'dark' ? value : systemMode()
}

function persistPreference(value) {
  try {
    localStorage.setItem(COLOR_PREFERENCE_KEY, value)
    localStorage.removeItem(LEGACY_COLOR_MODE_KEY)
  } catch {
    // Applying the theme is still useful when persistence is unavailable.
  }
}

function commitMode(value) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', value)
    document.documentElement.style.colorScheme = value
  }
  mode.value = value
  themeChangeHandler?.(value)
}

function canAnimate(from, to, animate) {
  return Boolean(
    animate
      && typeof document !== 'undefined'
      && from
      && from !== to
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
}

async function applyMode(value, { animate = true } = {}) {
  const current = typeof document === 'undefined'
    ? null
    : document.documentElement.getAttribute('data-theme')

  if (!canAnimate(current, value, animate)) {
    commitMode(value)
    return
  }

  if (typeof document.startViewTransition === 'function') {
    const transition = document.startViewTransition(async () => {
      commitMode(value)
      await nextTick()
    })
    try {
      await transition.finished
    } catch {
      // A cancelled view transition has already applied the new mode.
    }
    return
  }

  const app = document.getElementById('app') || document.body
  app.classList.add('theme-fade')
  app.style.opacity = '0'
  await new Promise((resolve) => window.setTimeout(resolve, 160))
  commitMode(value)
  await nextTick()
  app.style.opacity = '1'
  window.setTimeout(() => {
    app.classList.remove('theme-fade')
    app.style.opacity = ''
  }, 200)
}

export function setThemePreference(value, options) {
  const normalized = ['light', 'dark', 'system'].includes(value) ? value : 'system'
  preference.value = normalized
  persistPreference(normalized)
  return applyMode(resolvedMode(normalized), options)
}

function handleSystemChange() {
  const currentSystemMode = systemMode()

  // An explicit choice matching the OS no longer needs to be explicit.
  if (preference.value === 'light' || preference.value === 'dark') {
    if (preference.value === currentSystemMode) {
      setThemePreference('system')
    }
    return
  }
  applyMode(currentSystemMode)
}

function listenForSystemChanges() {
  if (typeof window === 'undefined' || !window.matchMedia || mediaQuery) return
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleSystemChange)
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(handleSystemChange)
  }
}

export function onThemeChange(handler) {
  themeChangeHandler = handler
  if (initialized) handler(mode.value)
}

export function initializeTheme() {
  if (!initialized) {
    initialized = true
    setThemePreference(storedPreference(), { animate: false })
    if (
      (preference.value === 'light' || preference.value === 'dark')
      && preference.value === systemMode()
    ) {
      setThemePreference('system', { animate: false })
    }
    listenForSystemChanges()
  }
  return mode
}

export function useTheme() {
  if (!initialized) initializeTheme()

  const isDark = computed(() => mode.value === 'dark')
  const isLight = computed(() => mode.value === 'light')
  const isSystem = computed(() => preference.value === 'system')

  function setMode(value) {
    return setThemePreference(
      value === 'system' || value === 'light' || value === 'dark' ? value : 'system',
    )
  }

  function toggleMode() {
    return preference.value === 'system'
      ? setThemePreference(mode.value === 'dark' ? 'light' : 'dark')
      : setThemePreference('system')
  }

  return { mode, preference, isDark, isLight, isSystem, setMode, toggleMode }
}

export function watchTheme(callback) {
  if (!initialized) initializeTheme()
  return watch(mode, callback, { immediate: true })
}
